import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentInvestmentGoalsData,
  type EnrollmentInvestmentGoalsSubmitInput,
  type EnrollmentInvestmentGoalsSubmitOutput,
} from '../../../models'
import { createMutationHook, createQueryHook } from '../../../hooks'

/**
 * Get Enrollment InvestmentGoals data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentInvestmentGoalsData>
 * @example const response = await getEnrollmentInvestmentGoalsData()
 */
export const getEnrollmentInvestmentGoalsData = async (
  context?: DefaultContext,
): Promise<EnrollmentInvestmentGoalsData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentInvestmentGoalsData',
      query: `mutation getEnrollmentInvestmentGoalsData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              userInfo {
                investment {
                  objective
                  experience
                }
              }
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentInvestmentGoalsData }
  return response.submitEnrollment
}

export const useGetEnrollmentInvestmentGoalsData = createQueryHook<undefined, EnrollmentInvestmentGoalsData>(
  getEnrollmentInvestmentGoalsData,
)

/**
 * Submit Enrollment InvestmentGoals data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentInvestmentGoalsSubmitOutput>
 * @example const response = await submitEnrollmentInvestmentGoalsData();
 */
export const submitEnrollmentInvestmentGoalsData = async (
  enrollmentInvestmentGoalsSubmitInput: EnrollmentInvestmentGoalsSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentInvestmentGoalsSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentInvestmentGoalsData',
      query: `mutation submitEnrollmentInvestmentGoalsData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentInvestmentGoalsSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentInvestmentGoalsSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentInvestmentGoalsData = createMutationHook<
  EnrollmentInvestmentGoalsSubmitInput,
  EnrollmentInvestmentGoalsSubmitOutput
>(submitEnrollmentInvestmentGoalsData)
